import TextButton, {
  Button as TextButtonContainer,
} from "@components/buttons/TextButton"
import { Shipping } from "@components/icons"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body14Styles, body16MainStyles } from "@constants/typography"
import { MobileAndDesktopOnly, TabletOnlyContainer } from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

const ESCAPE_KEY = "Escape"
const ENTER_KEY = "Enter"

const Container = styled.div`
  @media ${breakpoints.tablet} {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;

    ${TabletOnlyContainer} {
      width: 100%;
    }
  }
`

const LeftOnTablet = styled.div`
  @media ${breakpoints.tablet} {
    width: 50%;
  }
`

const ZipCodeForm = styled.div<{ showForm?: boolean }>`
  display: flex;
  width: 100%;
  height: 0;
  border: solid 1px ${colors.dividerLineGray};
  opacity: 0;
  pointer-events: none;
  ${({ showForm }) =>
    showForm &&
    `
    height: initial;
    margin-top: 40px;
    opacity: 1;
    pointer-events: initial;
  `}

  @media ${breakpoints.tablet} {
    ${({ showForm }) =>
      showForm &&
      `
    margin-top: 32px;
  `}
  }
`

const Input = styled.input`
  ${body14Styles}
  height: 48px;
  width: 50%;
  padding: 0 16px;
  color: ${colors.mediumGray};
  border: none;
  border-right: solid 1px ${colors.dividerLineGray};
  &:active {
    outline: none;
  }
  &:focus {
    outline: none;
  }

  ::-webkit-input-placeholder {
    color: ${colors.mediumGray};
  }
  ::-moz-placeholder {
    color: ${colors.mediumGray};
  }
  :-ms-input-placeholder {
    color: ${colors.mediumGray};
  }
  :-moz-placeholder {
    color: ${colors.mediumGray};
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const TextButtonWrapper = styled.div`
  width: 50%;

  ${TextButtonContainer} {
    padding: 3px 0 0 0;
    color: ${colors.offBlack};
    background: transparent;

    @media ${breakpoints.laptop} {
      transition: background-color ${animation.medium} ${cubicBezier};

      &:active {
        outline: none;
      }

      &:focus {
        outline: none;
      }

      &:hover {
        transition: background-color ${animation.quick} ${cubicBezier};
        background-color: ${colors.dividerLineGray};
        cursor: pointer;
      }
    }
  }
`

const DeliveryTextWrapper = styled.div`
  display: flex;
  margin: 40px 0 4px;
  flex-wrap: wrap;
  align-items: center;
`

const IconWrapper = styled.div`
  display: flex;
  width: 20px;
  height: 16px;
  margin-right: 12px;
`

const ShippingRow = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const ShippingTextWrapper = styled.div`
  margin-right: 14px;
`

const ShippingTextMargin = styled.div`
  height: 32px;
`

const ChangeZipButtonWrapper = styled.div`
  position: relative;
`

const ChangeZipButton = styled.button`
  ${body14Styles}
  padding: 14px;
  color: ${colors.mediumGray};
  transform: translate(-14px, -10px);
  background-color: transparent;
  border: none;

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  &:hover {
    cursor: pointer;
  }
`

const OutOfStockMessage = styled.div`
  ${body16MainStyles}
  display: flex;
  margin-top: 10px;
  align-content: center;
  justify-content: center;
  color: ${colors.brandOrange};

  @media ${breakpoints.tablet} {
    margin-top: 14px;
    justify-content: flex-start;
    font-size: 14px;
  }

  @media ${breakpoints.laptop} {
    ${body14Styles}
    justify-content: flex-start;
    color: ${colors.brandOrange};
  }
`

interface ShippingBlockProps {
  customerLocationFetched: boolean
  customerZipCode: string
  deliveryText?: React.ReactNode
  hideZipCodeForm: boolean
  invalidShippingData: boolean
  onUpdateZipCode?: (zipCode: string) => void
  outOfStockMessage?: string
  shippingDataLoaded: boolean
  shippingText: React.ReactNode
}

const ShippingBlock = (props: ShippingBlockProps) => {
  const [showForm, setShowForm] = React.useState<boolean>(false)
  const [zipCodeValue, setZipCodeValue] = React.useState<string>("")

  const handleShowForm = () => {
    setShowForm(true)

    const input = document.getElementById(
      "zipCodeInput"
    ) as HTMLInputElement | null

    if (input) {
      input.focus()
      input.select()
    }
  }

  const updateZipCode = () => {
    if (props.onUpdateZipCode && zipCodeValue) {
      props.onUpdateZipCode(zipCodeValue.toString())
    }
    setShowForm(false)
  }

  const onChange = ({ target: { value } }) => {
    setZipCodeValue(value)
  }

  const onKeyDown = (event: any) => {
    if (event.key === ESCAPE_KEY) {
      event.currentTarget.blur()
      setShowForm(false)
    }
    if (event.key === ENTER_KEY && event.currentTarget) {
      updateZipCode()
    }
  }

  React.useEffect(() => {
    setZipCodeValue(props.invalidShippingData ? "" : props.customerZipCode)
    if (
      props.customerLocationFetched &&
      props.customerZipCode &&
      !props.hideZipCodeForm
    ) {
      setShowForm(false)
    }
  }, [
    props.customerLocationFetched,
    props.customerZipCode,
    props.invalidShippingData,
  ])

  return (
    <Container>
      <LeftOnTablet>
        <ZipCodeForm showForm={!props.hideZipCodeForm && showForm}>
          <Input
            id="zipCodeInput"
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="Zip Code"
            type="number"
            value={zipCodeValue}
          />
          <TextButtonWrapper>
            <TextButton onClick={updateZipCode} text="Check Delivery" />
          </TextButtonWrapper>
        </ZipCodeForm>
        {!showForm && props.deliveryText ? (
          <>
            <DeliveryTextWrapper>
              <IconWrapper>
                <Shipping />
              </IconWrapper>
              {props.deliveryText}
            </DeliveryTextWrapper>
          </>
        ) : (
          <ShippingTextMargin />
        )}
        {!showForm && (
          <ShippingRow>
            <ShippingTextWrapper>{props.shippingText}</ShippingTextWrapper>
            {!props.hideZipCodeForm && props.shippingDataLoaded && (
              <ChangeZipButtonWrapper>
                <ChangeZipButton onClick={handleShowForm}>
                  Change Zip
                </ChangeZipButton>
              </ChangeZipButtonWrapper>
            )}
          </ShippingRow>
        )}
        {props.outOfStockMessage && (
          <MobileAndDesktopOnly>
            <OutOfStockMessage>{props.outOfStockMessage}</OutOfStockMessage>
          </MobileAndDesktopOnly>
        )}
      </LeftOnTablet>
      {props.outOfStockMessage && (
        <TabletOnlyContainer>
          <OutOfStockMessage>{props.outOfStockMessage}</OutOfStockMessage>
        </TabletOnlyContainer>
      )}
    </Container>
  )
}

export default ShippingBlock
